import React, { useState, useEffect } from 'react';
import Snackbar from './Snackbar';
import { getApiBaseUrl } from '../config/api';
import './SuperAdminManagement.css';

const SuperAdminManagement = ({ user, onShowNotification }) => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    role: 'super_admin',
    sendEmail: true
  });
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [roleFilter, setRoleFilter] = useState('all');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const API_BASE_URL = getApiBaseUrl();

  const showNotification = (message, type = 'info') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
    if (onShowNotification) {
      onShowNotification(message, type);
    }
  };

  const getAuthToken = () => {
    return localStorage.getItem('adminToken');
  };

  useEffect(() => {
    fetchAdmins();
  }, [pagination.page, roleFilter]);

  const fetchAdmins = async () => {
    setIsLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/super-admins?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setAdmins(data.admins || []);
        setPagination(data.pagination || pagination);
      } else {
        setError(data.error || 'Failed to fetch admins');
        showNotification(data.error || 'Failed to fetch admins', 'error');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admins');
      showNotification('Failed to load admins', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.first_name || !formData.last_name || !formData.username || !formData.email || !formData.password) {
      setError('All fields are required');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/super-admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          ...formData,
          sendEmail: !!formData.sendEmail
        })
      });

      const data = await response.json();
      if (data.success) {
        showNotification(data.message || 'Admin created successfully', 'success');
        setShowForm(false);
        setFormData({
          first_name: '',
          last_name: '',
          username: '',
          email: '',
          password: '',
          role: 'super_admin',
          sendEmail: true
        });
        fetchAdmins();
      } else {
        setError(data.error || 'Failed to create admin');
        showNotification(data.error || 'Failed to create admin', 'error');
      }
    } catch (err) {
      console.error('Error creating admin:', err);
      setError('Failed to create admin');
      showNotification('Failed to create admin', 'error');
    }
  };

  const handleToggleStatus = async (adminId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this admin?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/super-admins/${adminId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      const data = await response.json();
      if (data.success) {
        showNotification(data.message || `Admin ${action}d successfully`, 'success');
        fetchAdmins();
      } else {
        showNotification(data.error || `Failed to ${action} admin`, 'error');
      }
    } catch (err) {
      console.error(`Error ${action}ing admin:`, err);
      showNotification(`Failed to ${action} admin`, 'error');
    }
  };

  const handleResetPassword = async (adminId) => {
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/super-admins/${adminId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ password: passwordForm.password })
      });

      const data = await response.json();
      if (data.success) {
        showNotification('Password reset successfully', 'success');
        setShowPasswordModal(null);
        setPasswordForm({ password: '', confirmPassword: '' });
      } else {
        setError(data.error || 'Failed to reset password');
        showNotification(data.error || 'Failed to reset password', 'error');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to reset password');
      showNotification('Failed to reset password', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      super_admin: { class: 'role-super-admin', text: 'Super Admin', icon: '👑' },
      finance_admin: { class: 'role-finance-admin', text: 'Finance Admin', icon: '💰' },
      credit_control: { class: 'role-credit-control', text: 'Credit Control', icon: '📋' },
      underwriting: { class: 'role-underwriting', text: 'Underwriting', icon: '📝' }
    };
    const roleInfo = roleMap[role] || { class: 'role-admin', text: role, icon: '👤' };
    return (
      <span className={`role-badge ${roleInfo.class}`}>
        {roleInfo.icon} {roleInfo.text}
      </span>
    );
  };

  return (
    <div className="super-admin-management">
      <div className="user-management-header">
        <div className="header-content">
          <h2>👑 Super Admin Management</h2>
          <p>Manage super admins and finance team members</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="agent-filter-bar">
        <div className="filter-group">
          <label className="filter-label">
            <span className="filter-icon">🔍</span>
            Filter by Role:
          </label>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            className="role-filter-select"
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admins</option>
            <option value="finance_admin">Finance Admins</option>
            <option value="credit_control">Credit Control</option>
            <option value="underwriting">Underwriting</option>
          </select>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingAdmin(null);
            setFormData({
              first_name: '',
              last_name: '',
              username: '',
              email: '',
              password: '',
              role: 'super_admin'
            });
            setError('');
          }}
          className="btn-create-admin"
        >
          ➕ Create Admin
        </button>
      </div>

      {error && (
        <div className="error-alert">
          ⚠️ {error}
        </div>
      )}

      {showForm && (
        <div className="admin-form-card">
          <h3>{editingAdmin ? 'Edit Admin' : 'Create New Admin'}</h3>
          <form onSubmit={handleCreateAdmin}>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                />
                <small>Minimum 6 characters</small>
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="finance_admin">Finance Admin</option>
                  <option value="credit_control">Credit Control</option>
                  <option value="underwriting">Underwriting</option>
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!formData.sendEmail}
                    onChange={(e) => setFormData({ ...formData, sendEmail: e.target.checked })}
                  />
                  <span>Email credentials to user after creation</span>
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                💾 {editingAdmin ? 'Update' : 'Create'} Admin
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setError('');
                  setFormData({
                    first_name: '',
                    last_name: '',
                    username: '',
                    email: '',
                    password: '',
                    role: 'super_admin',
                    sendEmail: true
                  });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admins...</p>
        </div>
      ) : (
        <>
          <div className="agents-table-container">
            <table className="agents-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">
                      No admins found. {roleFilter !== 'all' ? 'Try adjusting your filter.' : 'Create your first admin user to get started.'}
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className={!admin.is_active ? 'inactive-agent' : ''}>
                      <td>
                        <div className="agency-info">
                          <strong>{admin.first_name} {admin.last_name}</strong>
                        </div>
                      </td>
                      <td>{admin.username}</td>
                      <td>{admin.email}</td>
                      <td>{getRoleBadge(admin.role)}</td>
                      <td>
                        <span className={`status-badge ${admin.is_active ? 'active' : 'inactive'}`}>
                          {admin.is_active ? '✅ Active' : '❌ Inactive'}
                        </span>
                      </td>
                      <td>{admin.created_by_name || admin.created_by_username || 'System'}</td>
                      <td>{formatDate(admin.last_login)}</td>
                      <td>{formatDate(admin.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleToggleStatus(admin.id, admin.is_active)}
                            className={`toggle-status-btn ${admin.is_active ? 'deactivate' : 'activate'}`}
                            disabled={admin.id === user?.id && !admin.is_active}
                            title={admin.id === user?.id && !admin.is_active ? 'Cannot deactivate yourself' : admin.is_active ? 'Deactivate admin' : 'Activate admin'}
                          >
                            {admin.is_active ? '🚫 Deactivate' : '✅ Activate'}
                          </button>
                          <button
                            onClick={() => {
                              setShowPasswordModal(admin.id);
                              setPasswordForm({ password: '', confirmPassword: '' });
                              setError('');
                            }}
                            className="btn-reset-password"
                          >
                            🔑 Reset Password
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total admins)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔑 Reset Password</h3>
              <button onClick={() => setShowPasswordModal(null)} className="close-btn">×</button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword(showPasswordModal);
              }}
            >
              <div className="form-group">
                <label>New Password *</label>
                <input
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                />
              </div>
              {error && <div className="error-alert">{error}</div>}
              <div className="form-actions">
                <button type="submit" className="btn-primary">Reset Password</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(null);
                    setPasswordForm({ password: '', confirmPassword: '' });
                    setError('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default SuperAdminManagement;

